/**
 * <TutorTodaySection> — « Aujourd'hui », les lives du jour du masteur, sur son accueil (template).
 *
 * La maquette A1 du shaping et le livre V16 (p. 45) : la séance qui vient en grand, avec
 * « GO ! » et « Préparer », SEULE dans le cadre des temps forts — c'est l'action du moment ;
 * puis les suivantes du jour en lignes ; et « Toute la semaine » en tête de section. L'anatomie est
 * celle de toute séance (`<NextSessionHero>`, `<SessionRow>`), à deux différences près, qui sont
 * celles du masteur (`tutorSessionAnatomy`) :
 *  - rien à la place de la capsule du tuteur : ce serait la sienne ;
 *  - sous la ligne horaire, sa ligne à lui : le type de cours et, s'il en a plusieurs, l'école
 *    (l'ancien `TutorLiveCardComponent`), sans « avec Camille » — « Cours partagé · 1h » —, puis ses
 *    élèves un par un, par leur nom, avec leur classe et leurs matières (`<TutorSessionRoster>`,
 *    « Jules M. · 4ᵉ · Maths »). Les matières vont aux élèves (Wajih, 29/10 : dans un cours partagé,
 *    chacun vient avec la sienne) ; la ligne de la séance ne garde les siennes que si aucun élève
 *    n'en porte (`subjectsByStudent`), et un élève sans matière à côté d'un autre qui en porte prend
 *    celles de la séance (`tutorRosterStudents`). Pas de pile de visages : les prénoms se lisaient sur l'ancienne
 *    carte, des initiales ne les remplacent pas (Wajih, 29/10).
 *
 * **La carte du live qui vient dit l'heure seule** (`timeOnly`) : la tête de l'accueil dit déjà la
 * date (« Jeudi 5 novembre · 3 lives aujourd'hui »), et sa chip dit « Aujourd'hui » et le décompte : la
 * date ne se dit qu'une fois (allègement du 05/11).
 *
 * **Chaque séance a son « GO ! »** (Wajih, 05/11 : « il faut absolument que je puisse rejoindre
 * n'importe quelle séance de ce jour ») — le mot et la règle de l'ancienne carte
 * (`TutorLiveCardComponent`) : plein le jour du live, il ouvre le précall de CETTE séance ; un autre
 * jour, secondaire, et l'écran dit « Le live n'est accessible que le jour même ». Sur la carte en
 * grand, sous ses infos (`joinDisplay="today"`, la voix tutoyée du masteur) ; sur chaque ligne, à
 * droite (`<SessionRow trailing>`, `<TutorSessionGo>`) — les suivantes du jour, celles déjà
 * commencées ou passées tant que la journée les liste, et les prochains lives d'un jour sans live.
 * Toucher une séance ouvre ce que l'ancien bouton « Gérer les RDV » ouvrait (déplacer la séance, ou
 * la boîte d'un retard ou d'un remplacement signalés) — c'est l'écran qui le décide
 * (`onSelectSession`) : « Gérer » est la ligne, « GO ! » est le bouton.
 *
 * **Un jour sans live, l'accueil n'est pas vide** : la section dit « Pas de live aujourd'hui » et
 * montre les trois prochains, en lignes qui disent leur jour (« Demain · 17h00 ») — l'ancienne
 * carte « Mes prochains lives » en montrait trois. Sans aucun live à venir (le nouveau masteur), la
 * phrase seule.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { pushButtonBoxHeight, pushButtonChromeWidth } from "../../atoms/Buttons/Button/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { CardList } from "../../molecules/CardList"
import { HighlightFrame } from "../../molecules/HighlightFrame"
import { List } from "../../molecules/List"
import { SessionDetailsLine, SessionDetailsLineSkeleton } from "../../molecules/SessionDetailsLine"
import { SessionRow } from "../../molecules/SessionRow"
import { SessionRowSkeleton } from "../../molecules/SessionRow/SessionRowSkeleton"
import { TutorSessionRoster, TutorSessionRosterSkeleton } from "../../molecules/TutorSessionRoster"
import { JOIN_LABEL, NextSessionHero, NextSessionHeroSkeleton } from "../../organisms/NextSessionHero"
import {
    isSessionDimmed,
    subjectsByStudent,
    tutorRosterStudents,
} from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { formatDayTime, isToday } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UITutorSession } from "../../../ports/primary/ui/types/tutorCoquilleAccueil"

const TEXTS = {
    today: "Aujourd'hui",
    upcoming: "Prochains lives",
    week: "Toute la semaine",
    prepare: "Préparer",
    noLiveToday: "Pas de live aujourd'hui.",
    noLiveAtAll: "Aucun live prévu pour l'instant.",
}

/** Les lives à venir qu'un jour sans live montre — les trois de l'ancienne carte. */
const UPCOMING_MAX = 3

/**
 * La séance telle que le masteur la lit, en props de `<NextSessionHero>` comme de `<SessionRow>` :
 * rien à la place de la capsule du tuteur, et sa ligne à lui à la place de la ligne de la séance.
 * La semaine (`<TutorLivesListPage>`) la pose aussi : une séance du masteur a une seule anatomie.
 */
export function tutorSessionAnatomy(session: UITutorSession) {
    return { session, lead: null, details: <TutorSessionDetails session={session} /> }
}

/** Ce que la ligne de la séance du masteur dit en plus de sa durée : le type de cours, l'école. */
export function tutorSessionDetails(session: UITutorSession): string[] {
    return [session.kindLabel, session.spaceLabel].filter((part): part is string => part != null && part !== "")
}

/**
 * La ligne de la séance du masteur : le type de cours et l'école (les matières seulement si aucun
 * élève ne porte les siennes), puis ses élèves, un par ligne. Elle s'estompe avec la séance, comme
 * la ligne de toute séance (`isSessionDimmed`).
 */
function TutorSessionDetails(props: { session: UITutorSession }) {
    const { session } = props
    const dimmed = isSessionDimmed(session)
    return (
        <Stack gap="1">
            <SessionDetailsLine
                session={session}
                withTutor={false}
                withSubjects={!subjectsByStudent(session.students)}
                extra={tutorSessionDetails(session)}
                dimmed={dimmed}
            />
            <TutorSessionRoster students={tutorRosterStudents(session)} dimmed={dimmed} />
        </Stack>
    )
}

/**
 * La forme de la ligne de la séance du masteur : « Cours partagé · 1h » sans chip (ses élèves portent
 * les matières), puis deux élèves — le cas courant, un cours partagé.
 */
function TutorSessionDetailsSkeleton() {
    return (
        <Stack gap="1">
            <SessionDetailsLineSkeleton width={110} withSubject={false} />
            <TutorSessionRosterSkeleton />
        </Stack>
    )
}

/** « GO ! » en Outfit 700 à `text-sm` : la largeur de son libellé, pour son squelette. */
const GO_LABEL_WIDTH = 30

export type TutorSessionGoProps = {
    /** La date de la séance : le jour du live, le bouton est plein. */
    date: string
    onPress: () => void
}

/**
 * « GO ! » sur la ligne d'une séance du masteur (`<SessionRow trailing>`), l'ancien bouton de chaque
 * carte : plein le jour du live, secondaire un autre jour — la règle que l'écran applique au geste
 * (`useTutorJoinSession`), lue au même instant. Le lecteur d'écran entend quelle séance il rejoint :
 * une liste en porte plusieurs.
 */
export function TutorSessionGo(props: TutorSessionGoProps) {
    const { date, onPress } = props
    return (
        <Button
            label={JOIN_LABEL.student}
            size="sm"
            variant={isToday(date) ? "solid" : "outline"}
            onPress={onPress}
            accessibilityLabel={`${JOIN_LABEL.student} — rejoindre la séance ${formatDayTime(date, true, "complement")}`}
        />
    )
}

/**
 * La forme d'une ligne de séance du masteur (`<SessionRow {...tutorSessionAnatomy}>`) : sans capsule, avec la place
 * de son « GO ! ».
 */
export function TutorSessionRowSkeleton(props: { timeOnly?: boolean }) {
    return (
        <SessionRowSkeleton
            timeOnly={props.timeOnly}
            lead={false}
            details={<TutorSessionDetailsSkeleton />}
            trailing={
                <Skeleton
                    shape="block"
                    height={pushButtonBoxHeight("sm")}
                    width={pushButtonChromeWidth("sm") + GO_LABEL_WIDTH}
                />
            }
        />
    )
}

export type TutorTodaySectionProps = {
    /** Les lives du jour, dans l'ordre : le premier en grand. */
    sessions: UITutorSession[]
    /** Un jour sans live : les prochains, dans l'ordre. Vide : la phrase seule. */
    upcoming?: UITutorSession[]
    /**
     * « GO ! » : le précall de cette séance le jour du live ; un autre jour, l'écran dit qu'on n'entre que
     * le jour même. Absent : pas de bouton.
     */
    onJoinSession?: (session: UITutorSession) => void
    /** « Préparer » : la préparation du live, sur une séance `preparable` seulement. Absent : pas de lien. */
    onPrepareSession?: (session: UITutorSession) => void
    /** Une séance touchée : l'écran ouvre ce que « Gérer les RDV » ouvrait. */
    onSelectSession?: (session: UITutorSession) => void
    /** « Toute la semaine » : la liste des lives. Absent : pas de lien. */
    onOpenWeek?: () => void
    testID?: string
}

export function TutorTodaySection(props: TutorTodaySectionProps) {
    const { sessions, upcoming = [], onJoinSession, onPrepareSession, onSelectSession, onOpenWeek, testID } = props
    const [next, ...later] = sessions
    const select = (session: UITutorSession) => (onSelectSession != null ? () => onSelectSession(session) : undefined)
    const go = (session: UITutorSession) =>
        onJoinSession != null ? (
            <TutorSessionGo date={session.date} onPress={() => onJoinSession(session)} />
        ) : undefined

    const week = onOpenWeek != null ? <TextButton label={TEXTS.week} onPress={onOpenWeek} size="sm" /> : undefined

    if (next == null) {
        const shown = upcoming.slice(0, UPCOMING_MAX)
        return (
            <List title={shown.length > 0 ? TEXTS.upcoming : TEXTS.today} headerRight={week} testID={testID}>
                <Stack gap="3">
                    <Text color="text-muted">{shown.length > 0 ? TEXTS.noLiveToday : TEXTS.noLiveAtAll}</Text>
                    {shown.length > 0 && (
                        <CardList>
                            {shown.map((session) => (
                                <SessionRow
                                    key={session.id}
                                    {...tutorSessionAnatomy(session)}
                                    relativeDay
                                    onPress={select(session)}
                                    trailing={go(session)}
                                />
                            ))}
                        </CardList>
                    )}
                </Stack>
            </List>
        )
    }

    return (
        <List title={TEXTS.today} headerRight={week} testID={testID}>
            <Stack gap="3">
                <HighlightFrame>
                    <CardList>
                        <NextSessionHero
                            {...tutorSessionAnatomy(next)}
                            joinDisplay="today"
                            voice="student"
                            timeOnly
                            onJoin={onJoinSession != null ? () => onJoinSession(next) : undefined}
                            onPress={select(next)}
                            secondaryAction={
                                onPrepareSession != null && next.preparable === true
                                    ? { label: TEXTS.prepare, onPress: () => onPrepareSession(next) }
                                    : undefined
                            }
                        />
                    </CardList>
                </HighlightFrame>
                {later.length > 0 && (
                    <CardList>
                        {later.map((session) => (
                            <SessionRow
                                key={session.id}
                                {...tutorSessionAnatomy(session)}
                                timeOnly
                                onPress={select(session)}
                                trailing={go(session)}
                            />
                        ))}
                    </CardList>
                )}
            </Stack>
        </List>
    )
}

/**
 * Sa forme dans le squelette de l'accueil : le cas courant, deux lives dans la journée — le premier
 * en grand dans son cadre avec la place de « GO ! », le second en ligne avec la sienne.
 */
export function TutorTodaySectionSkeleton() {
    return (
        <List title={TEXTS.today}>
            <Stack gap="3">
                <HighlightFrame>
                    <CardList>
                        <NextSessionHeroSkeleton join lead={false} timeOnly details={<TutorSessionDetailsSkeleton />} />
                    </CardList>
                </HighlightFrame>
                <CardList>
                    <TutorSessionRowSkeleton timeOnly />
                </CardList>
            </Stack>
        </List>
    )
}
